'use client';

import { useState, useMemo } from 'react';
import { Search, Plus, Eye, FileText, Trash2 } from 'lucide-react';
import { DataTable } from '../../../components/shared/DataTable';
import { PageHeader } from '../../../components/shared/PageHeader';
import { Modal, FormField, FormInput, FormSelect, FormTextarea, Button } from '../../../components/shared/Modal';
import { stockEntries, warehouses, items } from '../data/mock-data';
import {
  STOCK_ENTRY_TYPE_LABELS,
  STOCK_ENTRY_TYPE_COLORS,
  STOCK_ENTRY_STATUS_LABELS,
} from '../types';
import type { StockEntry, StockEntryType, StockEntryStatus } from '../types';

// 입출고 등록 모달
function StockEntryAddModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [entryItems, setEntryItems] = useState([{ itemId: '', qty: 1, unit: 'EA' }]);

  const addItem = () => setEntryItems([...entryItems, { itemId: '', qty: 1, unit: 'EA' }]);
  const removeItem = (index: number) => {
    if (entryItems.length > 1) setEntryItems(entryItems.filter((_, i) => i !== index));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="입출고 등록"
      description="재고 입고, 출고, 이동 내역을 등록합니다."
      size="xl"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button onClick={onClose}>등록</Button>
        </>
      }
    >
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <FormField label="전표번호" required>
            <FormInput placeholder="자동 생성" disabled />
          </FormField>
          <FormField label="유형" required>
            <FormSelect>
              <option value="">선택하세요</option>
              <option value="receive">입고</option>
              <option value="issue">출고</option>
              <option value="transfer">이동</option>
              <option value="adjustment">조정</option>
            </FormSelect>
          </FormField>
          <FormField label="출고 창고">
            <FormSelect>
              <option value="">선택하세요</option>
              {warehouses.filter(w => w.isActive).map((wh) => (
                <option key={wh.id} value={wh.id}>{wh.name}</option>
              ))}
            </FormSelect>
          </FormField>
          <FormField label="입고 창고">
            <FormSelect>
              <option value="">선택하세요</option>
              {warehouses.filter(w => w.isActive).map((wh) => (
                <option key={wh.id} value={wh.id}>{wh.name}</option>
              ))}
            </FormSelect>
          </FormField>
          <FormField label="전기일" required>
            <FormInput type="date" />
          </FormField>
          <FormField label="참조문서">
            <FormInput placeholder="PO-2024-XXX 또는 SO-2024-XXX" />
          </FormField>
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium">품목 목록</h3>
            <Button variant="outline" size="sm" onClick={addItem}>
              <Plus className="w-4 h-4 mr-1" />품목 추가
            </Button>
          </div>
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 font-medium">품목</th>
                  <th className="text-right p-3 font-medium w-24">수량</th>
                  <th className="text-center p-3 font-medium w-20">단위</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody>
                {entryItems.map((_, index) => (
                  <tr key={index} className="border-t border-border">
                    <td className="p-2">
                      <FormSelect>
                        <option value="">품목 선택</option>
                        {items.filter(i => i.isActive).map((item) => (
                          <option key={item.id} value={item.id}>{item.code} - {item.name}</option>
                        ))}
                      </FormSelect>
                    </td>
                    <td className="p-2">
                      <FormInput type="number" min={1} defaultValue={1} className="text-right" />
                    </td>
                    <td className="p-2">
                      <FormSelect>
                        <option value="EA">EA</option>
                        <option value="BOX">BOX</option>
                        <option value="KG">KG</option>
                        <option value="M">M</option>
                      </FormSelect>
                    </td>
                    <td className="p-2">
                      <button
                        onClick={() => removeItem(index)}
                        className="p-1 rounded hover:bg-accent"
                        disabled={entryItems.length === 1}
                      >
                        <Trash2 className="w-4 h-4 text-muted-foreground" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <FormField label="비고">
          <FormTextarea rows={2} placeholder="입출고 관련 메모를 입력하세요" />
        </FormField>
      </div>
    </Modal>
  );
}

export function StockEntryListPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<StockEntryType | 'all'>('all');
  const [selectedStatus, setSelectedStatus] = useState<StockEntryStatus | 'all'>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const filteredData = useMemo(() => {
    return stockEntries.filter((entry) => {
      // 검색 필터
      const matchesSearch =
        entry.entryNo.toLowerCase().includes(searchQuery.toLowerCase()) ||
        entry.itemCode.toLowerCase().includes(searchQuery.toLowerCase()) ||
        entry.itemName.toLowerCase().includes(searchQuery.toLowerCase());

      // 유형 필터
      const matchesType = selectedType === 'all' || entry.type === selectedType;

      // 상태 필터
      const matchesStatus =
        selectedStatus === 'all' || entry.status === selectedStatus;

      return matchesSearch && matchesType && matchesStatus;
    });
  }, [searchQuery, selectedType, selectedStatus]);

  const columns = [
    {
      key: 'entryNo',
      label: '전표번호',
      width: '15%',
      sortable: true,
      render: (value: unknown) => (
        <span className="font-mono text-sm">{value as string}</span>
      ),
    },
    {
      key: 'type',
      label: '유형',
      width: '8%',
      render: (value: unknown) => {
        const type = value as StockEntryType;
        return (
          <span
            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium text-white ${STOCK_ENTRY_TYPE_COLORS[type]}`}
          >
            {STOCK_ENTRY_TYPE_LABELS[type]}
          </span>
        );
      },
    },
    {
      key: 'itemName',
      label: '품목',
      width: '18%',
      sortable: true,
      render: (value: unknown, row: StockEntry) => (
        <div>
          <div className="font-medium truncate">{value as string}</div>
          <div className="text-xs text-muted-foreground">{row.itemCode}</div>
        </div>
      ),
    },
    {
      key: 'quantity',
      label: '수량',
      width: '10%',
      align: 'right' as const,
      sortable: true,
      render: (value: unknown, row: StockEntry) => (
        <span className="font-medium whitespace-nowrap">
          {(value as number).toLocaleString()} {row.unit}
        </span>
      ),
    },
    {
      key: 'warehouse',
      label: '창고',
      width: '18%',
      render: (_: unknown, row: StockEntry) => {
        if (row.type === 'transfer') {
          return (
            <div className="text-sm">
              <span>{row.fromWarehouseName}</span>
              <span className="text-muted-foreground mx-1">→</span>
              <span>{row.toWarehouseName}</span>
            </div>
          );
        }
        return (
          <span className="text-sm">
            {row.toWarehouseName || row.fromWarehouseName}
          </span>
        );
      },
    },
    {
      key: 'status',
      label: '상태',
      width: '8%',
      render: (value: unknown) => {
        const status = value as StockEntryStatus;
        const colors: Record<StockEntryStatus, string> = {
          draft: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
          submitted:
            'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
          completed:
            'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
          cancelled:
            'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
        };
        return (
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${colors[status]}`}
          >
            {STOCK_ENTRY_STATUS_LABELS[status]}
          </span>
        );
      },
    },
    {
      key: 'createdAt',
      label: '등록일',
      width: '10%',
      sortable: true,
      render: (value: unknown) => (
        <span className="text-sm text-muted-foreground whitespace-nowrap">
          {new Date(value as string).toLocaleDateString('ko-KR')}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: '8%',
      render: (_: unknown, row: StockEntry) => (
        <div className="flex items-center gap-1">
          <button className="p-1.5 rounded hover:bg-accent" title="상세보기">
            <Eye className="w-4 h-4 text-muted-foreground" />
          </button>
          <button className="p-1.5 rounded hover:bg-accent" title="인쇄">
            <FileText className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      ),
    },
  ];

  // 유형별 통계
  const stats = useMemo(() => {
    const types: StockEntryType[] = ['receive', 'issue', 'transfer', 'adjustment'];
    return types.map((type) => ({
      type,
      label: STOCK_ENTRY_TYPE_LABELS[type],
      count: stockEntries.filter((e) => e.type === type).length,
      color: STOCK_ENTRY_TYPE_COLORS[type],
    }));
  }, []);

  return (
    <div className="space-y-6">
      <PageHeader
        title="입출고 내역"
        description="재고 입출고 및 이동 내역을 관리합니다."
        actions={
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" />
            신규 등록
          </button>
        }
      />

      {/* 유형별 통계 - 모바일에서 스크롤 가능 */}
      <div className="overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0 scrollbar-none">
        <div className="grid grid-cols-4 gap-2 sm:gap-4 min-w-[500px] sm:min-w-0">
          {stats.map((stat) => (
            <button
              key={stat.type}
              onClick={() =>
                setSelectedType(selectedType === stat.type ? 'all' : stat.type)
              }
              className={`p-2 sm:p-4 rounded-lg border transition-all ${
                selectedType === stat.type
                  ? 'border-primary bg-primary/5'
                  : 'border-border bg-card hover:border-primary/50'
              }`}
            >
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full ${stat.color}`} />
                <span className="text-xs sm:text-sm font-medium whitespace-nowrap">{stat.label}</span>
              </div>
              <div className="text-lg sm:text-2xl font-bold mt-1 sm:mt-2">{stat.count}건</div>
            </button>
          ))}
        </div>
      </div>

      {/* 필터 영역 - 모바일 반응형 */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
        {/* 검색 */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="전표번호, 품목코드, 품목명 검색..."
            className="w-full h-9 sm:h-10 pl-10 pr-4 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* 필터 그룹 */}
        <div className="flex gap-2">
          {/* 유형 필터 */}
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value as StockEntryType | 'all')}
            className="flex-1 sm:flex-none h-9 sm:h-10 px-2 sm:px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">전체 유형</option>
            <option value="receive">입고</option>
            <option value="issue">출고</option>
            <option value="transfer">이동</option>
            <option value="adjustment">조정</option>
          </select>

          {/* 상태 필터 */}
          <select
            value={selectedStatus}
            onChange={(e) =>
              setSelectedStatus(e.target.value as StockEntryStatus | 'all')
            }
            className="flex-1 sm:flex-none h-9 sm:h-10 px-2 sm:px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">전체 상태</option>
            <option value="draft">초안</option>
            <option value="submitted">제출됨</option>
            <option value="completed">완료</option>
            <option value="cancelled">취소</option>
          </select>
        </div>
      </div>

      {/* 데이터 테이블 */}
      <DataTable
        data={filteredData}
        columns={columns}
        pageSize={10}
        emptyMessage="검색 결과가 없습니다."
      />

      {/* 입출고 등록 모달 */}
      <StockEntryAddModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}
